import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "rsuite";
import { PluggyConnect } from "react-pluggy-connect";
import { toast } from "react-toastify";
import api from "../../../../services/axios";
import { usePermissions } from "../../../../auth/PermissionProvider";
import { Permissions } from "../../../../auth/permissions";
import { ImportacaoExtrato, resumoImportacao } from "../../../../interfaces/Financial";
import {
  PluggyConexao as Conexao,
  PluggyConnectToken,
  PluggyStatus,
} from "../../../../interfaces/Pluggy";

interface Props {
  /** Chamado depois de uma sincronização bem-sucedida, para recarregar os não identificados. */
  onSincronizado: (resumo: ImportacaoExtrato) => void | Promise<void>;
}

const BASE = "/Financial/Pluggy";
const toastOpts = { position: "top-right", autoClose: 4000 } as const;

// O backend responde texto puro nos erros de regra e { mensagem } nos de autorização.
const mensagemDe = (error: any, padrao: string) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data) return data;
  if (typeof data?.mensagem === "string" && data.mensagem) return data.mensagem;
  return padrao;
};

const fmtDataHora = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
// "yyyy-MM-dd" (DateOnly) sem passar por Date: evita o fuso empurrar a data para o dia anterior.
const fmtData = (iso: string) => iso.slice(0, 10).split("-").reverse().join("/");

const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};


const btnPrimary =
  "rounded-xl bg-dv-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-dv-sm transition-all hover:bg-dv-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-dv-blue-500 dark:hover:bg-dv-blue-600";
const btnGhost =
  "rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5";
const inputCls =
  "rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:border-white/10 dark:bg-navy-900 dark:text-white";
const boxCls = "mt-5 rounded-xl border border-gray-200 p-4 dark:border-white/10";

/**
 * Conexão bancária automática (Pluggy / Open Finance) da Conciliação: conecta o banco, escolhe a
 * conta e puxa o extrato sem passar pelo OFX. Quem só vê o financeiro sincroniza; conectar,
 * desconectar e registrar o webhook são de `financial.admin`.
 */
const PluggyConexao: React.FC<Props> = ({ onSincronizado }) => {
  const { can } = usePermissions();
  const admin = can(Permissions.FinancialAdmin);

  const [status, setStatus] = useState<PluggyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [connectToken, setConnectToken] = useState<string | null>(null);
  const [itemReconectar, setItemReconectar] = useState<string | undefined>(undefined);
  const [escolhendo, setEscolhendo] = useState<Conexao | null>(null);
  const [contaEscolhida, setContaEscolhida] = useState("");
  const [desconectando, setDesconectando] = useState(false);
  const [dataInicio, setDataInicio] = useState("");
  const [relendo, setRelendo] = useState(false);


  const carregar = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.get<PluggyStatus>(`${BASE}/Status`);
      setStatus(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const conexao = status?.conexoes?.find((c) => c.ativa) ?? null;
  const inicioGravado = conexao?.dtInicioExtrato?.slice(0, 10) ?? "";
  useEffect(() => {
    setDataInicio(inicioGravado);
  }, [inicioGravado]);

  const aguardandoConta = escolhendo ?? (conexao && !conexao.accountId && conexao.contasDisponiveis?.length > 1 ? conexao : null);

  const abrirConnect = async (itemId?: string) => {
    setErro(null);
    setOcupado(true);
    try {
      const { data } = await api.post<PluggyConnectToken>(`${BASE}/ConnectToken`, itemId ? { itemId } : {});
      setItemReconectar(itemId);
      setConnectToken(data.accessToken);
    } catch (error: any) {
      setErro(mensagemDe(error, "Não foi possível abrir a conexão com o banco."));
    } finally {
      setOcupado(false);
    }
  };

  const fecharConnect = () => {
    setConnectToken(null);
    setItemReconectar(undefined);
  };

  const aoConectar = async ({ item }: { item: { id: string } }) => {
    fecharConnect();
    setOcupado(true);
    try {
      const { data } = await api.post<Conexao>(`${BASE}/Conexoes`, { itemId: item.id });
      // Mais de uma conta e nenhuma escolhida: a pessoa decide qual extrato puxar.
      if (!data.accountId && data.contasDisponiveis?.length > 1) {
        setEscolhendo(data);
        setContaEscolhida("");
      }
      toast.success("Banco conectado.", toastOpts);
      await carregar();
    } catch (error: any) {
      setErro(mensagemDe(error, "Falha ao registrar a conexão com o banco."));
    } finally {
      setOcupado(false);
    }
  };

  const salvarConta = async () => {
    if (!aguardandoConta || !contaEscolhida) return;
    setOcupado(true);
    setErro(null);
    try {
      await api.put<Conexao>(`${BASE}/Conexoes/${aguardandoConta.id}/Conta`, { accountId: contaEscolhida });
      setEscolhendo(null);
      toast.success("Conta definida.", toastOpts);
      await carregar();
    } catch (error: any) {
      setErro(mensagemDe(error, "Falha ao definir a conta."));
    } finally {
      setOcupado(false);
    }
  };

  const sincronizar = async () => {
    if (!conexao) return;
    setErro(null);
    setSincronizando(true);
    try {
      const { data } = await api.post<ImportacaoExtrato>(`${BASE}/Conexoes/${conexao.id}/Sincronizar`);
      toast.success(resumoImportacao(data), toastOpts);
      await onSincronizado(data);
    } catch (error: any) {
      setErro(mensagemDe(error, "Falha ao sincronizar com o banco."));
    } finally {
      setSincronizando(false);
      await carregar();
    }
  };

  const relerDesde = async () => {
    if (!conexao || !dataInicio) return;
    setErro(null);
    setRelendo(true);
    try {
      const { data } = await api.put<ImportacaoExtrato>(`${BASE}/Conexoes/${conexao.id}/Inicio`, { dataInicio });
      toast.success(resumoImportacao(data), toastOpts);
      await onSincronizado(data);
    } catch (error: any) {
      setErro(mensagemDe(error, "Falha ao reler o extrato."));
    } finally {
      setRelendo(false);
      await carregar();
    }
  };

  const desconectar = async () => {
    if (!conexao) return;
    setOcupado(true);
    setErro(null);
    try {
      await api.delete(`${BASE}/Conexoes/${conexao.id}`);
      setDesconectando(false);
      toast.success("Banco desconectado. Os lançamentos já importados continuam.", toastOpts);
      await carregar();
    } catch (error: any) {
      setErro(mensagemDe(error, "Falha ao desconectar o banco."));
      setDesconectando(false);
    } finally {
      setOcupado(false);
    }
  };

  const registrarWebhook = async () => {
    setOcupado(true);
    try {
      await api.post(`${BASE}/Webhook/Registrar`);
      toast.success("Webhook registrado no Pluggy.", toastOpts);
    } catch (error: any) {
      toast.error(mensagemDe(error, "Falha ao registrar o webhook."), toastOpts);
    } finally {
      setOcupado(false);
    }
  };

  if (loading && !status) {
    return <div className="mt-5 h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" aria-busy="true" aria-label="Carregando conexão bancária" />;
  }

  if (loadError || !status) {
    return (
      <div
        role="alert"
        className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
      >
        Não foi possível consultar a conexão bancária automática.{" "}
        <button type="button" className="font-semibold underline" onClick={carregar}>
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!status.habilitado) {
    return (
      <p className="mt-5 rounded-xl border border-gray-200 p-4 text-sm text-gray-600 dark:border-white/10 dark:text-gray-400">
        Conexão bancária automática (Pluggy) desligada — o administrador precisa configurar as credenciais.
      </p>
    );
  }

  return (
    <div className={boxCls}>
      <h3 className="text-sm font-bold text-navy-700 dark:text-white">Conexão bancária automática</h3>

      {conexao ? (
        <div className="mt-2 flex flex-col gap-2">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {conexao.nomeInstituicao ?? conexao.banco}
            {conexao.nomeConta ? ` · ${conexao.nomeConta}` : ""}
          </p>
          {conexao.ultimoErro ? (
            <p role="alert" className="break-words text-sm text-red-700 dark:text-red-300">
              Última sincronização falhou: {conexao.ultimoErro}
            </p>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {conexao.dtInicioExtrato ? `Extrato desde ${fmtData(conexao.dtInicioExtrato)} · ` : ""}
              {conexao.dtUltimaSincronizacao
                ? `Última sincronização: ${fmtDataHora(conexao.dtUltimaSincronizacao)}`
                : "Ainda não sincronizado."}
            </p>
          )}

          {aguardandoConta && admin && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex flex-col gap-1">
                <label htmlFor="pluggy-conta" className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Conta do extrato
                </label>
                <select
                  id="pluggy-conta"
                  className={inputCls}
                  value={contaEscolhida}
                  onChange={(e) => setContaEscolhida(e.target.value)}
                >
                  <option value="">Escolha a conta</option>
                  {aguardandoConta.contasDisponiveis.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                      {c.numero ? ` · ${c.numero}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className={btnPrimary} disabled={!contaEscolhida || ocupado} onClick={salvarConta}>
                Salvar conta
              </button>
            </div>
          )}
          {!conexao.accountId && !aguardandoConta && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Nenhuma conta escolhida ainda. Use "Reconectar" para escolher a conta do extrato.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={btnPrimary}
              disabled={sincronizando || ocupado || !conexao.accountId}
              onClick={sincronizar}
            >
              {sincronizando ? "Sincronizando..." : "Sincronizar agora"}
            </button>
            {admin && (
              <>
                {(conexao.ultimoErro || !conexao.accountId) && (
                  <button type="button" className={btnGhost} disabled={ocupado} onClick={() => abrirConnect(conexao.itemId ?? undefined)}>
                    Reconectar
                  </button>
                )}
                <button type="button" className={btnGhost} disabled={ocupado} onClick={registrarWebhook}>
                  Registrar webhook
                </button>
                <button type="button" className={btnGhost} disabled={ocupado} onClick={() => setDesconectando(true)}>
                  Desconectar
                </button>
              </>
            )}
          </div>
          {admin && conexao.accountId && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex flex-col gap-1">
                <label htmlFor="pluggy-inicio" className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Trazer extrato desde
                </label>
                <input
                  id="pluggy-inicio"
                  type="date"
                  className={inputCls}
                  value={dataInicio}
                  max={hojeIso()}
                  onChange={(e) => setDataInicio(e.target.value)}
                />
              </div>
              <button
                type="button"
                className={btnGhost}
                disabled={!dataInicio || relendo || ocupado || sincronizando}
                onClick={relerDesde}
              >
                {relendo ? "Relendo..." : "Reler extrato"}
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 sm:self-center">
                Relê o extrato desde a data; o que já veio não duplica. O Pluggy guarda até 12 meses.
              </p>
            </div>
          )}

        </div>
      ) : (
        <div className="mt-2 flex flex-col gap-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {admin
              ? "Nenhum banco conectado. Conecte para receber os créditos sem importar OFX."
              : "Nenhum banco conectado. Peça a um administrador para conectar."}
          </p>
          {admin && (
            <div>
              <button type="button" className={btnPrimary} disabled={ocupado} onClick={() => abrirConnect()}>
                Conectar banco
              </button>
            </div>
          )}
        </div>
      )}

      {erro && (
        <p
          role="alert"
          className="mt-3 break-words rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300"
        >
          {erro}
        </p>
      )}

      {connectToken && (
        <PluggyConnect
          connectToken={connectToken}
          updateItem={itemReconectar}
          includeSandbox={process.env.NODE_ENV !== "production"}
          theme={document.body.classList.contains("dark") ? "dark" : "light"}
          language="pt"
          onSuccess={aoConectar}
          onError={(e) => {
            fecharConnect();
            setErro(e.message || "A conexão com o banco não foi concluída.");
          }}
          onClose={fecharConnect}
        />
      )}

      <Modal open={desconectando} onClose={() => setDesconectando(false)} size="xs" aria-labelledby="pluggy-desconectar-titulo">
        <Modal.Header>
          <Modal.Title id="pluggy-desconectar-titulo">Desconectar o banco?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          O NINA para de puxar o extrato desta conta. Os lançamentos já importados continuam na conciliação.
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className={btnGhost} onClick={() => setDesconectando(false)}>
            Cancelar
          </button>{" "}
          <button type="button" className={btnPrimary} disabled={ocupado} onClick={desconectar}>
            Desconectar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PluggyConexao;
